import { get, has, merge } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  CHART_COLORS_SEQUENTIAL,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

export default {
  name: 'Colored Bar',
  displayName: 'Colored',
  order: 3,
  flavour: 'discrete-quantitative-series',
  category: CHART_CATEGORIES.BAR,
  iconValue: 'bar-color',
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: false,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'color',
      displayName: 'Color',
      required: true,
      channelType: 'aggregation',
      intent: 'quantitative-series',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const logScaleOnValueAxis = get(
      customisations.axes,
      'x.logScale.enabled',
      false
    );
    const valueAxisProps = getScaleMinMaxProps(
      customisations,
      'x',
      logScaleOnValueAxis
    );
    const color = get(
      customisations.options.colorContinuous,
      'value.color',
      CHART_COLORS_SEQUENTIAL[0]
    );
    const reverseColor = get(
      customisations.options.colorContinuous,
      'value.reverse',
      false
    );
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('x'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
            ...(!encodings.color
              ? []
              : [
                  {
                    type: 'filter',
                    expr: 'datum["color"] !== null && !isNaN(datum["color"])',
                  },
                ]),
          ],
        },
      ],
      marks: [
        {
          name: 'marks',
          type: 'rect',
          style: ['bar'],
          from: { data: 'transformedData' },
          encode: {
            update: {
              y: { scale: 'y', field: 'y' },
              x: { scale: 'x', field: 'x' },
              x2: {
                scale: 'x',
                value: logScaleOnValueAxis ? valueAxisProps.domainMin : 0,
              },
              height: { scale: 'y', band: true },
              tooltip: {
                signal: generateVegaTooltip(encodings, customisations),
              },
              fill: encodings.color
                ? { scale: 'color', field: 'color' }
                : { value: CHART_COLORS[0] },
            },
          },
        },
        ...(!get(customisations.options, 'dataValueLabels.enabled')
          ? []
          : [
              {
                type: 'text',
                name: 'valueLabels',
                from: { data: 'transformedData' },
                encode: {
                  enter: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.x,
                        customisations.channels.x,
                        { field: 'x' }
                      ),
                    },
                    y: {
                      scale: 'y',
                      field: 'y',
                      offset: { signal: "bandwidth('y') / 2" },
                    },
                    x: { scale: 'x', field: 'x' },
                    baseline: { value: 'middle' },
                    dx: { value: 3 },
                  },
                },
              },
            ]),
      ],
      scales: [
        {
          name: 'x',
          type: logScaleOnValueAxis ? 'log' : 'linear',
          domain: { data: 'transformedData', field: 'x' },
          range: [0, { signal: 'width' }],
          zero: !logScaleOnValueAxis,
          ...valueAxisProps,
        },
        {
          name: 'y',
          type: 'band',
          domain: { data: 'transformedData', field: 'y', sort: sortParams.y },
          range: [0, { signal: 'height' }],
          paddingInner: 0.1,
          paddingOuter: 0.05,
        },
        {
          name: 'color',
          type: 'sequential',
          domain: { data: 'transformedData', field: 'color' },
          range: { scheme: color },
          nice: false,
          zero: false,
          reverse: reverseColor,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: true,
          title: get(encodings, 'x.axis.title', ''),
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: false,
          title: get(encodings, 'y.axis.title', ''),
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            {
              title: get(encodings, 'color.legend.title'),
              type: 'gradient',
              fill: 'color',
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color,
                        { withDecimals: false }
                      ),
                    },
                  },
                },
              },
            },
          ],
    };
  },
};
